import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Calendar, MessageCircle, Eye, Edit3, Check, Search, Bell, AlertTriangle,
  Phone, EyeOff, CheckCircle, ListChecks
} from "lucide-react";
import { toast } from "sonner";
import CurrencyInput from "@/components/CurrencyInput";

// ─────────────────────────────────────────────────────────────────────────────
// Agenda do Crediário — 5 buckets: overdue, today, soon (5d), upcoming, paid.
// Filtros: range em "upcoming" e "paid" são controlados pelo backend.
// "Pendentes" → link pra /clientes?pending=1 (visão completa de pendentes).
// ─────────────────────────────────────────────────────────────────────────────
const onlyDigits = (s) => (s || "").replace(/\D+/g, "");
const fmtBR = (iso) => {
  try { return new Date(iso + "T12:00:00").toLocaleDateString("pt-BR"); } catch { return iso; }
};
const yearMonthNow = () => new Date().toISOString().slice(0, 7);

const UPCOMING_RANGES = [
  { v: "7d", label: "7 dias" },
  { v: "15d", label: "15 dias" },
  { v: "30d", label: "30 dias" },
  { v: "month", label: "Este mês" },
  { v: "6m", label: "6 meses" },
  { v: "1y", label: "1 ano" },
  { v: "all", label: "Todas" },
];
const PAID_RANGES = [
  { v: "last7d", label: "Últimos 7 dias" },
  { v: "last15d", label: "Últimos 15 dias" },
  { v: "last30d", label: "Últimos 30 dias" },
  { v: "lastmonth", label: "Último mês" },
  { v: "last6m", label: "Últimos 6 meses" },
  { v: "lastyear", label: "Último ano" },
  { v: "all", label: "Todas" },
];

function StatusBadge({ status }) {
  const map = {
    open: { txt: "Em aberto", cls: "bg-neutral-100 text-neutral-700 dark:bg-white/10 dark:text-neutral-200" },
    partial: { txt: "Pago parcial", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
    paid: { txt: "Pago", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  };
  const m = map[status] || map.open;
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${m.cls}`}>{m.txt}</span>;
}

function PayDialog({ open, onClose, card, onPaid }) {
  const [mode, setMode] = useState("full");
  const [val, setVal] = useState(0);
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setMode("full"); setVal(card?.remaining || 0); setNote(""); } }, [open, card]);
  if (!open || !card) return null;
  const amt = mode === "full" ? card.remaining : Number(val || 0);
  const submit = async () => {
    if (amt <= 0) return toast.error("Valor deve ser maior que zero");
    try {
      await api.post(`/installment-plans/${card.plan_id}/installments/${card.installment_id}/pay`,
        { amount: amt, note });
      toast.success("Pagamento registrado");
      onPaid && onPaid();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar pagamento");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pay-installment-modal">
        <DialogHeader><DialogTitle>Confirmar pagamento</DialogTitle></DialogHeader>
        <div className="text-xs text-neutral-500">
          Parcela {card.installment_index}/{card.installments_total} · vence {fmtBR(card.due_date)}
        </div>
        <div className="text-sm">Cliente: <strong>{card.customer_name}</strong></div>
        <div className="flex gap-2 mt-2">
          <button onClick={()=>setMode("full")} className={`flex-1 text-xs py-1.5 rounded border ${mode==="full"?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-[#E5E5E5]"}`} data-testid="pay-mode-full">Valor cheio ({brl(card.remaining)})</button>
          <button onClick={()=>setMode("other")} className={`flex-1 text-xs py-1.5 rounded border ${mode==="other"?"bg-[#0A0A0A] text-white border-[#0A0A0A]":"border-[#E5E5E5]"}`} data-testid="pay-mode-other">Outro valor</button>
        </div>
        {mode === "other" && (
          <div className="mt-2">
            <Label className="text-xs">Valor recebido</Label>
            <CurrencyInput value={val} onChange={setVal} data-testid="pay-other-amount"/>
          </div>
        )}
        <div className="mt-2">
          <Label className="text-xs">Observação (opcional)</Label>
          <Input value={note} onChange={e=>setNote(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pay-submit"><Check size={14} className="mr-1"/> Confirmar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VisualizarDialog({ open, onClose, card }) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (!open || !card) return;
    api.get(`/customers/${card.customer_id}/installment-plans`)
      .then(r => setPlan((r.data || []).find(p => p.id === card.plan_id) || null))
      .catch(() => {});
  }, [open, card]);
  if (!open || !card) return null;
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="view-installment-modal">
        <DialogHeader><DialogTitle>Detalhes do parcelamento</DialogTitle></DialogHeader>
        <div className="text-sm space-y-1">
          <div><strong>Cliente:</strong> {card.customer_name}</div>
          {card.order_number && <div className="text-xs text-neutral-500">Pedido #{String(card.order_number).padStart(6, "0")}</div>}
        </div>
        {plan && (
          <div className="mt-3 border-t pt-3 space-y-1">
            <div className="text-xs text-neutral-500">Total: {brl(plan.total_amount)} · {plan.kind === "equal" ? "Parcelas iguais" : "Personalizadas"}</div>
            {(plan.installments || []).map((p, idx) => (
              <div key={p.id} className={`flex justify-between text-xs py-1.5 border-b border-[#F0F0F0] last:border-0 ${p.id === card.installment_id ? "bg-amber-50 dark:bg-amber-950/20 px-2 -mx-2 rounded" : ""}`}>
                <span>{idx+1}/{plan.installments.length} · {fmtBR(p.due_date)}</span>
                <span className="flex items-center gap-2">{brl(p.amount)} <StatusBadge status={p.status}/></span>
              </div>
            ))}
            {(plan.history || []).length > 0 && (
              <div className="mt-2 text-[10px] text-neutral-500">
                <div className="font-medium uppercase tracking-wider mb-1">Histórico</div>
                {plan.history.map((h, i) => (
                  <div key={i}>{h.at?.slice(0, 16).replace("T", " ")} · {h.detail} ({h.by})</div>
                ))}
              </div>
            )}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Fechar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgendaCard({ card, bucket, onChange, store }) {
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [busy, setBusy] = useState(false);
  // Fallback p/ Safari: quando window.open é bloqueado pelo navegador
  // (popup blocker depois de await), guardamos a URL aqui e abrimos um
  // diálogo com botão <a target="_blank"> que NUNCA é bloqueado.
  const [waFallback, setWaFallback] = useState(null);

  const styles = {
    overdue: { border: "border-l-red-500", label: "VENCIDA", color: "text-red-600" },
    today: { border: "border-l-amber-500", label: "VENCE HOJE", color: "text-amber-600" },
    soon: { border: "border-l-orange-500", label: "PRÓXIMA", color: "text-orange-600" },
    upcoming: { border: "border-l-emerald-500", label: "A VENCER", color: "text-emerald-700 dark:text-emerald-400" },
    paid: { border: "border-l-emerald-700", label: "PAGA", color: "text-emerald-700 dark:text-emerald-400" },
  }[bucket] || {};

  const hideThisMonth = async () => {
    if (!window.confirm("Tirar esta parcela da agenda apenas no mês atual? Volta no próximo.")) return;
    setBusy(true);
    try {
      await api.post(`/installment-plans/${card.plan_id}/installments/${card.installment_id}/exclude-month`,
        { year_month: yearMonthNow() });
      toast.success("Parcela escondida da agenda deste mês");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao esconder");
    } finally { setBusy(false); }
  };

  const openWhats = () => {
    const phone = onlyDigits(card.customer_phone);
    if (!phone) return toast.error("Cliente sem telefone cadastrado");
    window.open(`https://wa.me/${phone.length === 11 ? "55" + phone : phone}`, "_blank", "noopener,noreferrer");
  };

  // ── COBRAR ──────────────────────────────────────────────────────────
  // Mesmo padrão do botão "Cobrar" de Pedidos.jsx (que funciona ok em prod):
  // monta a URL do wa.me 100% síncrono (sem await/api), usando o
  // payment_token que já vem dentro de cada card da agenda. Sem aba
  // em branco e sem bloqueio de popup.
  // Fallback: se o backend (produção com cache/deploy parcial) ainda não
  // mandar o token na agenda, busca via /installments/{iid}/pix e abre
  // depois — sem pré-abrir tab em branco.
  const openCobranca = async () => {
    const phone = onlyDigits(card.customer_phone);
    if (!phone) return toast.error("Cliente sem telefone cadastrado");
    if (!store?.pix_key) {
      toast.error("⚠ Cadastre a chave PIX em 'Dados da Loja' antes de cobrar.");
      return;
    }
    let token = card.payment_token;
    if (!token) {
      // Fallback resiliente — backend antigo / cache de produção sem backfill
      setBusy(true);
      try {
        const r = await api.get(`/installments/${card.installment_id}/pix`,
          { params: { plan_id: card.plan_id } });
        token = r.data?.payment_token || "";
      } catch (e) {
        const detail = e?.response?.data?.detail || "";
        if (detail.includes("chave PIX")) {
          toast.error("⚠ Cadastre a chave PIX em 'Dados da Loja' antes de cobrar.");
        } else {
          toast.error(detail || "Erro ao gerar link de pagamento");
        }
        setBusy(false);
        return;
      } finally { setBusy(false); }
    }
    if (!token) {
      toast.error("Não foi possível gerar o link de pagamento. Tente novamente.");
      return;
    }
    const payLink = `${window.location.origin}/pagamento-parcela/${token}`;
    const dueFmt = fmtBR(card.due_date);
    const overdue = bucket === "overdue";
    const msg = overdue
      ? `Olá! Como está?\n\nSeu pagamento com vencimento em ${dueFmt} está em atraso.\n\n📌 Ao fazer o pagamento, nos envie o comprovante aqui para darmos baixa.\n\nSegue o link para pagamento:\n${payLink}`
      : `Olá! Como está?\n\nSua conta no valor de ${brl(card.remaining)} vence dia ${dueFmt}.\n\n📌 Ao fazer o pagamento, nos envie o comprovante aqui para darmos baixa.\n\nSegue o link para pagamento:\n${payLink}`;
    const phoneFull = phone.length === 11 ? "55" + phone : phone;
    const waUrl = `https://wa.me/${phoneFull}?text=${encodeURIComponent(msg)}`;
    const popup = window.open(waUrl, "_blank", "noopener,noreferrer");
    // Safari: depois de await, window.open pode retornar null (bloqueado).
    // Nesse caso, mostra dialog com link real <a target="_blank"> que SEMPRE
    // funciona (clique direto = user gesture novo).
    if (!popup) {
      setWaFallback({ url: waUrl, link: payLink });
      try { await navigator.clipboard.writeText(payLink); } catch {}
    }
  };

  const goEditBalance = () => navigate(`/clientes?open=${card.customer_id}`);

  const showButtons = bucket !== "paid";

  return (
    <>
      <div className={`bg-white dark:bg-[#0A0A0A] border border-[#E5E5E5] dark:border-white/10 border-l-4 ${styles.border} rounded-lg p-3 space-y-2`} data-testid={`agenda-card-${bucket}-${card.installment_id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{card.customer_name}</div>
            <div className="text-[11px] text-neutral-500 inline-flex items-center gap-1"><Phone size={10}/> {card.customer_phone || "—"}</div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider font-medium ${styles.color}`}>{styles.label}</span>
        </div>
        <div className="flex items-baseline justify-between">
          <div className="text-lg font-display">{brl(card.remaining || card.amount)}</div>
          <div className="text-[11px] text-neutral-500">
            {card.installment_index}/{card.installments_total} · {bucket === "paid" ? `pago em ${card.paid_at ? new Date(card.paid_at).toLocaleDateString("pt-BR") : "—"}` : `vence ${fmtBR(card.due_date)}`}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <StatusBadge status={card.status}/>
          {card.order_number && <span className="text-[10px] text-neutral-400">Pedido #{String(card.order_number).padStart(6, "0")}</span>}
        </div>
        {showButtons && (
          <div className="grid grid-cols-2 gap-1.5 pt-1">
            <Button size="sm" onClick={()=>setPaying(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" data-testid={`btn-pay-${card.installment_id}`}><Check size={12} className="mr-1"/> Confirmar pgto</Button>
            <Button size="sm" variant="outline" onClick={openCobranca} disabled={busy} className="h-8 text-xs" data-testid={`btn-cobrar-${card.installment_id}`}><MessageCircle size={12} className="mr-1"/> Cobrar</Button>
            <Button size="sm" variant="outline" onClick={openWhats} className="h-8 text-xs"><Phone size={12} className="mr-1"/> Conversar</Button>
            <Button size="sm" variant="outline" onClick={()=>setViewing(true)} className="h-8 text-xs"><Eye size={12} className="mr-1"/> Visualizar</Button>
            <Button size="sm" variant="outline" onClick={goEditBalance} className="h-8 text-xs"><Edit3 size={12} className="mr-1"/> Editar saldo</Button>
            <Button size="sm" variant="outline" onClick={hideThisMonth} disabled={busy} className="h-8 text-xs" data-testid={`btn-hide-${card.installment_id}`}><EyeOff size={12} className="mr-1"/> Tirar agenda</Button>
          </div>
        )}
        {!showButtons && (
          <Button size="sm" variant="outline" onClick={()=>setViewing(true)} className="h-8 text-xs w-full"><Eye size={12} className="mr-1"/> Visualizar</Button>
        )}
      </div>
      <PayDialog open={paying} onClose={()=>setPaying(false)} card={card} onPaid={onChange}/>
      <VisualizarDialog open={viewing} onClose={()=>setViewing(false)} card={card}/>
      {/* Fallback Safari/popup-blocker: mostra link com <a target="_blank">
          que SEMPRE funciona (clique direto = user gesture novo). */}
      <Dialog open={!!waFallback} onOpenChange={(o)=>!o && setWaFallback(null)}>
        <DialogContent className="max-w-sm" data-testid="cobrar-fallback-modal">
          <DialogHeader>
            <DialogTitle>Mensagem pronta para enviar</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-neutral-500 space-y-2">
            <p>Seu navegador bloqueou a abertura automática do WhatsApp. Toque no botão abaixo para enviar:</p>
            <p className="break-all bg-neutral-50 dark:bg-white/5 border border-[#E5E5E5] rounded p-2 text-[10px] font-mono">{waFallback?.link}</p>
            <p className="text-[10px] text-neutral-400">(O link já foi copiado para sua área de transferência.)</p>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <a
              href={waFallback?.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={()=>setTimeout(()=>setWaFallback(null), 100)}
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md py-2 text-sm font-medium"
              data-testid="cobrar-fallback-open-wa"
            >
              <MessageCircle size={14}/> Abrir WhatsApp
            </a>
            <Button variant="outline" onClick={()=>setWaFallback(null)} className="w-full">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Crediario() {
  const navigate = useNavigate();
  const [agenda, setAgenda] = useState({ overdue: [], today: [], soon: [], upcoming: [], paid: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [store, setStore] = useState({});
  // Defaults conforme spec: "A vencer" abre como "30d" toda vez que entra
  // (não persiste). "Pagas" abre como "lastmonth".
  const [upcomingRange, setUpcomingRange] = useState("30d");
  const [paidRange, setPaidRange] = useState("lastmonth");

  const load = useCallback(async (uRange, pRange) => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/installments/agenda", { params: { upcoming_range: uRange, paid_range: pRange } }),
        api.get("/store").catch(() => ({ data: {} })),
      ]);
      setAgenda(r.data);
      setStore(s.data || {});
    } catch (e) {
      toast.error("Erro ao carregar agenda");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(upcomingRange, paidRange); }, [load, upcomingRange, paidRange]);

  const filterBy = (list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(c => (c.customer_name || "").toLowerCase().includes(q));
  };

  const totalToReceive = useMemo(() => {
    const all = [...agenda.overdue, ...agenda.today, ...agenda.soon, ...agenda.upcoming];
    return all.reduce((s, c) => s + Number(c.remaining || 0), 0);
  }, [agenda]);

  // 1ª linha: Vencidas / Vence hoje / Próximas 5 dias
  const row1 = [
    { key: "overdue", title: "Vencidas", icon: AlertTriangle, color: "text-red-600", items: filterBy(agenda.overdue) },
    { key: "today", title: "Vence hoje", icon: Calendar, color: "text-amber-600", items: filterBy(agenda.today) },
    { key: "soon", title: "Próximos 5 dias", icon: Bell, color: "text-orange-600", items: filterBy(agenda.soon) },
  ];

  return (
    <div className="space-y-6 p-2 sm:p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny mb-2">Crediário</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Agenda de cobranças</h1>
          <p className="text-xs text-neutral-500 mt-1">Total a receber em aberto: <span className="font-medium text-neutral-900 dark:text-neutral-100">{brl(totalToReceive)}</span></p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={()=>navigate("/clientes?pending=1")} className="h-9 text-xs" data-testid="btn-go-pendentes">
            <ListChecks size={14} className="mr-1.5"/> Pendentes (todos)
          </Button>
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
            <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar cliente…" className="pl-9" data-testid="agenda-search"/>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-neutral-400 text-sm py-8">Carregando…</div>
      ) : (
        <>
          {/* 1ª linha — 3 cards "imediatos" */}
          <div className="grid lg:grid-cols-3 gap-4">
            {row1.map(b => (
              <Card key={b.key} className="border-[#E5E5E5] dark:border-white/10 shadow-none p-4 space-y-3" data-testid={`bucket-${b.key}`}>
                <div className="flex items-center justify-between">
                  <h2 className={`text-sm font-display flex items-center gap-2 ${b.color}`}><b.icon size={14}/> {b.title}</h2>
                  <span className="text-[10px] uppercase tracking-wider bg-neutral-100 dark:bg-white/10 px-2 py-0.5 rounded-full">{b.items.length}</span>
                </div>
                <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
                  {b.items.length === 0 && <div className="text-xs text-neutral-400 italic text-center py-4">Nenhuma parcela aqui.</div>}
                  {b.items.map(card => <AgendaCard key={card.installment_id} card={card} bucket={b.key} store={store} onChange={()=>load(upcomingRange, paidRange)}/>)}
                </div>
              </Card>
            ))}
          </div>

          {/* 2ª linha — A vencer (com filtro) + Pagas (com filtro) */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-[#E5E5E5] dark:border-white/10 shadow-none p-4 space-y-3 border-l-4 border-l-emerald-500" data-testid="bucket-upcoming">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-display flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                  <Calendar size={14}/> A vencer
                  <span className="text-[10px] uppercase tracking-wider bg-neutral-100 dark:bg-white/10 px-2 py-0.5 rounded-full">
                    {filterBy(agenda.upcoming).length}
                  </span>
                </h2>
                <Select value={upcomingRange} onValueChange={setUpcomingRange}>
                  <SelectTrigger className="h-8 text-xs w-[150px]" data-testid="upcoming-range-select">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    {UPCOMING_RANGES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
                {filterBy(agenda.upcoming).length === 0 && <div className="text-xs text-neutral-400 italic text-center py-4">Nenhuma parcela aqui.</div>}
                {filterBy(agenda.upcoming).map(card => <AgendaCard key={card.installment_id} card={card} bucket="upcoming" store={store} onChange={()=>load(upcomingRange, paidRange)}/>)}
              </div>
            </Card>

            <Card className="border-[#E5E5E5] dark:border-white/10 shadow-none p-4 space-y-3 border-l-4 border-l-emerald-700" data-testid="bucket-paid">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-display flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle size={14}/> Pagas
                  <span className="text-[10px] uppercase tracking-wider bg-neutral-100 dark:bg-white/10 px-2 py-0.5 rounded-full">
                    {filterBy(agenda.paid).length}
                  </span>
                </h2>
                <Select value={paidRange} onValueChange={setPaidRange}>
                  <SelectTrigger className="h-8 text-xs w-[170px]" data-testid="paid-range-select">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    {PAID_RANGES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
                {filterBy(agenda.paid).length === 0 && <div className="text-xs text-neutral-400 italic text-center py-4">Nenhuma parcela aqui.</div>}
                {filterBy(agenda.paid).map(card => <AgendaCard key={card.installment_id} card={card} bucket="paid" store={store} onChange={()=>load(upcomingRange, paidRange)}/>)}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
